import React from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Alert, Box, Button, Card, CardContent, Chip, Stack, TextField, Typography } from '@mui/material';

import ImageAdjustmentControls from './ImageAdjustmentControls';
import PendingImageCropEditor from './PendingImageCropEditor';
import { CropAreaPixels, PendingCropMode, PendingImageAdjustments, PendingImageItem } from './pendingUploadTypes';

interface Props {
  descriptionLabel: string;
  disabled?: boolean;
  item: PendingImageItem;
  showDescriptionField?: boolean;
  onAdjustmentsChange: (localId: string, nextAdjustments: PendingImageAdjustments) => void;
  onCropChange: (localId: string, nextCropArea: CropAreaPixels, nextZoom: number) => void;
  onCropModeChange: (localId: string, nextCropMode: PendingCropMode) => void;
  onDescriptionChange: (localId: string, value: string) => void;
  onRemove: (localId: string) => void;
  onReset: (localId: string) => void;
  onUpload: (localId: string) => void;
}

export default function PendingImageTile({
  descriptionLabel,
  disabled = false,
  item,
  showDescriptionField = true,
  onAdjustmentsChange,
  onCropChange,
  onCropModeChange,
  onDescriptionChange,
  onRemove,
  onReset,
  onUpload,
}: Props) {
  const isUploading = item.status === 'uploading';
  const isFailed = item.status === 'failed';

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        minHeight: 470,
        borderColor: isFailed ? 'error.light' : 'info.light',
        borderStyle: 'dashed',
        overflow: 'visible',
      }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', pb: 3 }}>
        <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1}>
          <Stack alignItems="center" direction="row" spacing={1} sx={{ minWidth: 0, flexWrap: 'wrap' }}>
            <Chip
              color={isFailed ? 'error' : 'info'}
              icon={isFailed ? <WarningAmberOutlinedIcon /> : undefined}
              label={isFailed ? 'Upload failed' : isUploading ? 'Uploading' : 'Pending upload'}
              size="small"
            />
          </Stack>

          <Stack alignItems="center" direction="row" spacing={1}>
            <Button
              color="error"
              disabled={disabled || isUploading}
              size="small"
              startIcon={<DeleteOutlineIcon />}
              variant="text"
              onClick={() => onRemove(item.localId)}
            >
              Remove
            </Button>
          </Stack>
        </Stack>

        <PendingImageCropEditor disabled={disabled || isUploading} item={item} onCropChange={onCropChange} />

        <ImageAdjustmentControls
          adjustments={item.adjustments}
          cropMode={item.cropMode}
          disabled={disabled || isUploading}
          fixedAspectRatio={item.fixedAspectRatio}
          onAdjustmentsChange={(nextAdjustments) => onAdjustmentsChange(item.localId, nextAdjustments)}
          onCropModeChange={(nextCropMode) => onCropModeChange(item.localId, nextCropMode)}
          onReset={() => onReset(item.localId)}
        />

        <Stack spacing={0.5}>
          <Typography noWrap variant="subtitle2">
            {item.filename}
          </Typography>
        </Stack>

        {showDescriptionField ? (
          <TextField
            disabled={disabled || isUploading}
            fullWidth
            label={descriptionLabel}
            maxRows={3}
            minRows={1}
            multiline
            size="small"
            value={item.description}
            onChange={(event) => onDescriptionChange(item.localId, event.target.value)}
          />
        ) : null}

        {item.errorMessage ? <Alert severity="error">{item.errorMessage}</Alert> : null}

        <Box sx={{ pt: 1 }}>
          <Button
            disabled={disabled || isUploading}
            fullWidth
            startIcon={<UploadOutlinedIcon />}
            variant="contained"
            onClick={() => onUpload(item.localId)}
          >
            {isFailed ? 'Retry upload' : isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
